import React from 'react';
import { useSelector } from 'react-redux';

import type { ToolCall } from 'js/src/agent/types';
import { Spinner } from 'js/src/components/common/ui/Spinner';
import { isToolboxCreatingVideo } from 'js/src/data/app';
import { getVideoDraftById } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';

interface BlankVideoStatusIndicatorProps {
    toolCall: ToolCall;
}

/**
 * Header status icon for the create_blank_video tool call. The tool returns its
 * result immediately (it only sets up the blank video), but the agent keeps
 * building scenes into it for the rest of the turn. Drive "done-ness" off the
 * build state rather than the create call's own result, so the spinner runs for
 * the whole build: the draft's isGenerating flag (multi-draft Sketch flow) or
 * the toolbox creating-video flag (single-video /app/ flow).
 */
export const BlankVideoStatusIndicator: React.FC<BlankVideoStatusIndicatorProps> = ({
    toolCall,
}) => {
    const isBusy = useSelector(
        (state: RootState) =>
            (getVideoDraftById(state, toolCall.id)?.isGenerating ?? false) ||
            isToolboxCreatingVideo(state),
    );

    if (isBusy) {
        return (
            <span data-testid="blank-video-status-spinner" className="d-inline-flex">
                <Spinner size="sm" className="text-muted me-l5-4" />
            </span>
        );
    }

    return (
        <i
            translate="no"
            className="material-symbols-outlined me-l5-4 text-success"
            data-testid="blank-video-status-done"
        >
            done_outline
        </i>
    );
};
